import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchChatConversations,
  type ChatConversationSummary,
} from '@/services/chatConversations';

interface SearchResults {
  query: string;
  items: ChatConversationSummary[];
  hasMore: boolean;
  offset: number;
}

interface ChatHistorySearch {
  items: ChatConversationSummary[];
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  retry: () => void;
}

/** Search all history without replacing the normal sidebar's loaded pages. */
export const useChatHistorySearch = (
  query: string,
  history: ChatConversationSummary[]
): ChatHistorySearch => {
  const search = query.trim();
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const request = useRef<AbortController | null>(null);
  const moreRequest = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    request.current = controller;
    moreRequest.current?.abort();
    moreRequest.current = null;
    setResults(null);
    setError(null);
    setIsLoadingMore(false);
    setIsLoading(Boolean(search));

    if (!search) return () => controller.abort();

    const timer = window.setTimeout(() => {
      void fetchChatConversations({ search }, controller.signal)
        .then((page) => {
          if (controller.signal.aborted) return;

          setResults({
            query: search,
            items: page.conversations,
            hasMore: page.hasMore,
            offset: page.conversations.length,
          });
        })
        .catch(() => {
          if (!controller.signal.aborted)
            setError('Chats could not be searched. Try again.');
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsLoading(false);
        });
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
      moreRequest.current?.abort();
    };
    // Renaming, pinning, or deleting a row refreshes the filtered result too.
  }, [search, history, retryCount]);

  const loadMore = useCallback(async () => {
    if (
      !results?.hasMore ||
      results.query !== search ||
      moreRequest.current ||
      isLoading
    )
      return;

    const controller = new AbortController();
    moreRequest.current = controller;
    const currentRequest = request.current;
    setIsLoadingMore(true);
    setError(null);

    try {
      const page = await fetchChatConversations(
        { search, skip: results.offset },
        controller.signal
      );

      if (controller.signal.aborted || request.current !== currentRequest)
        return;

      setResults((current) => {
        if (!current || current.query !== search) return current;
        const seen = new Set(current.items.map((item) => item.id));

        return {
          query: search,
          items: [
            ...current.items,
            ...page.conversations.filter((item) => !seen.has(item.id)),
          ],
          hasMore: page.hasMore,
          offset: results.offset + page.conversations.length,
        };
      });
    } catch {
      if (!controller.signal.aborted)
        setError('More matching chats could not be loaded. Try again.');
    } finally {
      if (moreRequest.current === controller) {
        moreRequest.current = null;
        setIsLoadingMore(false);
      }
    }
  }, [isLoading, results, search]);

  return {
    items: results?.query === search ? results.items : [],
    hasMore: results?.query === search && results.hasMore,
    isLoading:
      Boolean(search) && (isLoading || results?.query !== search) && !error,
    isLoadingMore,
    error,
    loadMore,
    retry: () => setRetryCount((count) => count + 1),
  };
};
